import React, { useEffect } from 'react';
import { ParentContainer } from '../../styles';
import { CLOSE_POPUP_ICON, PENDING_PAYMENT_VERIFICATION_ICON } from '@Images';
import {
  A2FormMessage,
  FlexContainer,
  InstructionsContainer,
  ReceiverContainer,
  Text
} from './styles';
import { Grid } from 'semantic-ui-react';
import { FONT_FAMILY, COLORS } from '@Commons';
import { InstaremAvatar, ModalFactory, CommonButton, Divider } from '@Components';
import FundTransfer from './fund-transfer';
import { FUND_YOUR_TRANSFER_SCREEN } from '../../utils/constants';
import { IStore } from '../../../../store.type';
import { connect } from 'react-redux';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import {
  getNiumForexTransactionDetails,
  getNiumForexPayInBanks,
  changeNiumForexScreen
} from '@Redux/actions/nium-forex';
import {
  IPayInBankDetails,
  IFundYourTransferScreenProps,
  IVerifyPaymentTransaction,
  TimeObject,
  INiumForexCMSData
} from '../../utils/interface';
import { get } from 'lodash';
import {
  formatCurrencyAmount,
  calculateRemainingTime,
  formatCurrencyToTwoDecimalPlaces
} from '@Utils/library';
import { NF_SCREENS } from '../../root/screenConstants';
import { NF_GO_TO_HOME_EVENT_CT } from '@Utils/constants/eventsConstants';
import { FLOW_TYPE } from '@Utils/constants';
import { ModalWrapper } from '../acknowledgement/Style';
import NFCustomInfoCarouselCard from '../../components/NFCustomInfoCard';
import { logUserEventForNFCrossSelling } from '../cross-selling/utils';
import { EventNames, LOG_TYPE } from '../../../../utils/events/eventsConstants';
import { getCMSDataForA2FormMessage } from '../../utils/util';
import { logEvent } from '../../../../utils/events/logUserEvents';

const FundYourTransferScreen = (props: IFundYourTransferScreenProps): React.ReactElement => {
  const {
    getNiumForexTransactionDetails,
    activateLoader,
    disableLoader,
    transactionId,
    getNiumForexPayInBanks,
    countryCode,
    sourceCurrency,
    changeNiumForexScreen,
    nfCrossSellBanner
  } = props;

  const [transactionData, setTransactionData] = React.useState<IVerifyPaymentTransaction>();
  const [cmsData, setCMSData] = React.useState<INiumForexCMSData>();
  const [timeRemaining, setTimeRemaining] = React.useState<TimeObject>({
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const handleInfoCardClick = () => {
    logUserEventForNFCrossSelling({
      eventName: EventNames.NF_CROSS_SELLING.discovery,
      pageName: NF_SCREENS.fund_transfer
    });
    changeNiumForexScreen(NF_SCREENS.cross_selling);
  };

  const showModal = async () => {
    //@ts-ignore
    await ModalFactory.builder(
      (onClose: any) => (
        <ModalWrapper>
          <img
            width={24}
            height={24}
            src={CLOSE_POPUP_ICON}
            alt="close"
            className="closeIcon"
            onClick={() => {
              onClose(false);
            }}
          />
          <div className="modal-heading">{FUND_YOUR_TRANSFER_SCREEN.MODAL.heading}</div>
          <div className="modal-subheading">
            {FUND_YOUR_TRANSFER_SCREEN.MODAL.body.replace(
              'XX',
              timeRemaining?.hours.toString() || '0'
            )}
          </div>
          <CommonButton
            text={FUND_YOUR_TRANSFER_SCREEN.MODAL.gotIt}
            onClick={() => {
              onClose(false);
              changeNiumForexScreen(NF_SCREENS.send_money);
            }}
            width={450}
            height={46}
          />
        </ModalWrapper>
      ),
      'insta-modal center-modal',
      true
    );
  };

  useEffect(() => {
    const loadTransaction = async () => {
      activateLoader();
      const trData = await getNiumForexTransactionDetails(transactionId);
      if (trData?.data?.success) {
        setTransactionData(trData?.data?.data);
        const { created_at, cancellation_duration, is_referral } = trData?.data?.data || '';
        const remainingTime = calculateRemainingTime(created_at, cancellation_duration);
        if (remainingTime) setTimeRemaining(remainingTime);
        if (is_referral) {
          const res = await getCMSDataForA2FormMessage();
          setCMSData(res?.labels);
        }
      }
      disableLoader();
    };
    loadTransaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePaymentInstruction = async () => {
    activateLoader();
    const payInData = await getNiumForexPayInBanks(countryCode, sourceCurrency);
    const payinBankDetails: IPayInBankDetails[] = get(
      payInData,
      'data.data.payinBankDetails',
      null
    );
    disableLoader();
    if (!payinBankDetails || !transactionData) {
      return;
    }
    const remainingTime = calculateRemainingTime(
      transactionData?.created_at,
      transactionData?.cancellation_duration
    );
    // @ts-ignore
    return await ModalFactory.builder?.(
      (onClose: any) => (
        <FundTransfer
          onClose={onClose}
          payInBankDetails={payinBankDetails}
          transactionDetails={transactionData}
          remainingTime={remainingTime}
          isReferral={transactionData?.is_referral}
        />
      ),
      'center-modal insta-modal',
      true
    );
  };

  const handleCrossSellBannerOnclick = () => {
    changeNiumForexScreen(NF_SCREENS.cross_selling);
  };

  const onGoHome = () => {
    const ctData = {
      pageName: NF_SCREENS.fund_transfer
    };
    logEvent(NF_GO_TO_HOME_EVENT_CT, FLOW_TYPE.TRANSACTION, ctData, LOG_TYPE.CLEVER_TAP);
    showModal();
    // changeNiumForexScreen(NF_SCREENS.send_money);
  };

  return (
    <ParentContainer padding={[48, 10, 48, 10]}>
      <img src={PENDING_PAYMENT_VERIFICATION_ICON} alt="verify-payment" />
      <Text padding="16px 0 8px 0px">
        {transactionData?.is_referral
          ? FUND_YOUR_TRANSFER_SCREEN.headingVerify
          : FUND_YOUR_TRANSFER_SCREEN.heading}
      </Text>
      <Grid>
        <Grid.Row>
          <Text fontSize="24px">
            {formatCurrencyAmount(
              formatCurrencyToTwoDecimalPlaces(transactionData?.net_source_amount)
            )}
          </Text>
          <Text fontSize="14px" padding="8px 0 2px 5px">
            {transactionData?.source_currency || ''}
          </Text>
        </Grid.Row>
      </Grid>
      <Text fontFamily={FONT_FAMILY.hellix.regular} fontSize="14px" lineHeight="22px">
        {`${FUND_YOUR_TRANSFER_SCREEN.transactionNumber}: ${
          transactionData?.transaction_number || ''
        }`}
      </Text>
      <ReceiverContainer>
        <InstaremAvatar
          name={transactionData?.beneficiary?.full_name || ''}
          flagCode={transactionData?.beneficiary?.nationality}
        />
        <Text fontSize="16px" fontFamily={FONT_FAMILY.hellix.regular} lineHeight="22px">
          {`${transactionData?.beneficiary?.full_name || ''} ${
            FUND_YOUR_TRANSFER_SCREEN.willReceive
          }`}
        </Text>
        <Text fontSize="16px" lineHeight="26px">
          {`${formatCurrencyAmount(
            formatCurrencyToTwoDecimalPlaces(transactionData?.destination_amount)
          )} ${transactionData?.destination_currency || ''}`}
        </Text>
      </ReceiverContainer>
      <InstructionsContainer>
        <Text
          lineHeight="22px"
          fontSize="14px"
          fontFamily={FONT_FAMILY.hellix.regular}
          textAlign="start"
        >
          {FUND_YOUR_TRANSFER_SCREEN.alreadyPaidInfo}
        </Text>
        <Divider height="1px" color={COLORS.lightGrey}></Divider>
        <FlexContainer row>
          <FlexContainer>
            <Text fontSize="14px" lineHeight="22px" textAlign="start" align="start">
              {FUND_YOUR_TRANSFER_SCREEN.depositMoney}
            </Text>
            <Text
              fontSize="12px"
              fontFamily={FONT_FAMILY.hellix.regular}
              lineHeight="18px"
              align="start"
            >
              {FUND_YOUR_TRANSFER_SCREEN.ignoreIfPaid}
            </Text>
          </FlexContainer>
          <FlexContainer>
            <Text
              fontSize="14px"
              lineHeight="22px"
              color={COLORS.darkBlue}
              align="end"
              cursor="pointer"
              onClick={handlePaymentInstruction}
            >
              {FUND_YOUR_TRANSFER_SCREEN.paymentInfoBtn}
            </Text>
          </FlexContainer>
        </FlexContainer>
      </InstructionsContainer>
      <CommonButton
        text={FUND_YOUR_TRANSFER_SCREEN.gotoHomeBtn}
        width={450}
        onClick={onGoHome}
        margin="0 0 48px"
      />
      <NFCustomInfoCarouselCard
        showSeparator={true}
        borderRadius={8}
        ctaText={nfCrossSellBanner.labels?.ctaButton}
        borderColor={COLORS.darkBlue}
        showCta={true}
        heading={nfCrossSellBanner.title}
        subHeading={nfCrossSellBanner.description}
        icon={nfCrossSellBanner.icon}
        handleOnClick={handleInfoCardClick}
        padding="0 0 0 16px"
        ctaOnclick={handleCrossSellBannerOnclick}
      />
      {transactionData?.is_referral && (
        <A2FormMessage>
          {cmsData?.a2FormMessage || FUND_YOUR_TRANSFER_SCREEN.a2FormMessage}
        </A2FormMessage>
      )}
    </ParentContainer>
  );
};

function mapStateToProps(reduxState: IStore) {
  const {
    rootReducer: {
      userStatusReducer: { country_code: countryCode },
      niumForexReducer: {
        niumForexCrossSelling: {
          components: { nf_cross_sell_discovery_banner: nfCrossSellBanner }
        },
        transactionDetails: { transactionId, sourceCurrency }
      }
    }
  } = reduxState;
  return {
    transactionId,
    countryCode,
    sourceCurrency,
    nfCrossSellBanner
  };
}

export default connect(mapStateToProps, {
  activateLoader,
  disableLoader,
  getNiumForexTransactionDetails,
  getNiumForexPayInBanks,
  changeNiumForexScreen
  // @ts-ignore
})(FundYourTransferScreen);
